import ActivityOptions from '~/components/shared/ActivityOptions';
import AdjustmentDialogs from '~/components/shared/AdjustmentDialogs';
import {
  PartSelection,
  updateExistingPart,
} from '~/components/shared/PartEditor';
import {
  ActivityRow,
  HistoryAge,
  WarrantyInfoDialog,
} from '~/components/shared/ActivityDetails';
import trace from '~/lib/trace';
import React, {useEffect, useState} from 'react';
import {View, ScrollView, ActivityIndicator} from 'react-native';
import {Divider} from 'react-native-paper';
import isFinite from 'lodash/isFinite';
import pickBy from 'lodash/pickBy';
import moment from 'moment';
import sy from '~/styles';
import {
  Text,
  RadioButton,
  Pressable,
  RobTaskIcon,
  Dialog,
  Checkbox,
  Quantity,
  PriceCheck,
  ArticleText,
  SlaArticleText,
  ActionButton,
  Grouping,
  DurationText,
  DurationIcon,
} from '~/components/controls';
import Actionsheet, {
  Title,
  Content,
  Actions,
} from '~/components/controls/Actionsheet';
import {HOUR_TYPES as DURATION_HOUR_TYPES} from '~/components/shared/Dialogs/duration';
import {
  service_request as serviceRequestApi,
  rob as robApi,
  user as userApi,
} from '~/api/private';
import {
  AddArticleActionsheet,
  RobCodeActionsheet,
  LeniencyActionsheet,
} from '~/components/shared/Actionsheets';
import {
  DamageNotAllowedDialog,
  DurationDialog,
  QuantityDialog,
} from '~/components/shared/Dialogs';
import Format from '~/lib/format';
import {
  isRobLeniencyTask,
  isRobTaskDenied,
  isRobTaskChanged,
} from '~/types/rob';
import withDimensions from '~/components/hoc/with-dimensions';
import {SOURCES} from '~/types/sources';
import {useDispatch, useSelector} from '~/lib/hooks';
import {setUserSettings} from '~/actions';
import DirectionsCarIcon from '~/images/md-icons/directions_car/materialicons/24px.svg';
import FactoryOutlinedIcon from '~/images/md-icons/factory/materialsymbolsoutlined/24px.svg';
import EditOutlinedIcon from '~/images/md-icons/edit/materialsymbolsoutlined/24px.svg';
import HistoryIcon from '~/images/md-icons/history/materialicons/24px.svg';
import HistoryToggleOffIcon from '~/images/md-icons/history_toggle_off/materialicons/24px.svg';
import DiscountIcon from '~/images/md-icons/discount/materialsymbolsoutlined/24px.svg';
import AddIcon from '~/images/md-icons/add/materialicons/24px.svg';
import RestartIcon from '~/images/md-icons/restart/materialicons/24px.svg';
import InfoIcon from '~/images/md-icons/info/materialicons/24px.svg';
import SellIcon from '~/images/md-icons/sell/materialsymbolsoutlined/24px.svg';
import ProductionQuantityLimitsIcon from '~/images/md-icons/production_quantity_limits/materialsymbolsoutlined/24px.svg';
import PropTypes from 'prop-types';
const SCREENS = {
  TASK: 'TASK',
  WORK: 'WORK',
  ROB_CODE: 'ROB_CODE',
};
const ROB_OPERATION_UITVOEREN = '00';
const ROB_REASON_NVT = '99';
const buildMaintenanceChildDisplayText = (item, child) => {
  const parentQualifierIds = new Set(item.qualifiers.map((q) => q.id));
  return child.qualifiers
    .filter((q) => !parentQualifierIds.has(q.id))
    .map((q) => q.text)
    .join(', ');
};
const normalizeMaintenanceItems = (maintenance) =>
  Object.values(maintenance ?? {})
    .sort((a, b) => a.text.localeCompare(b.text))
    .map((item) => ({
      ...item,
      children: item.children
        ?.map((child) => ({
          ...child,
          display_text: buildMaintenanceChildDisplayText(item, child),
        }))
        .sort((a, b) => a.display_text.localeCompare(b.display_text)),
    }));
export const calculateMaintenanceAmount = ({
  selected,
  selectedAdditional,
  additional,
  hourlyRate,
  durations,
  markups,
  discounts,
  partsMarkups,
  partsDiscounts,
}) => {
  if (selected?.warranty === 'Warranty') {
    return 0;
  }
  if (isRobLeniencyTask(selected?.warranty)) {
    return selected?.leniency_amount;
  }
  const task_amount = Math.max(
    hourlyRate * durations[selected.id] +
      (markups[selected.id] ?? 0) -
      (discounts[selected.id] ?? 0),
    0,
  );
  const parts_amount = Object.values(selected.parts ?? {})
    .filter((item) => isFinite(item.price) && isFinite(item.quantity))
    .reduce(
      (acc, item) =>
        acc +
        Math.max(
          item.price * item.quantity +
            (partsMarkups[item.attributes.itemMpId] ?? 0) -
            (partsDiscounts[item.attributes.itemMpId] ?? 0),
          0,
        ),
      0,
    );
  let amount = Format.round(task_amount) + Format.round(parts_amount);
  if (selectedAdditional) {
    amount += selectedAdditional.reduce((acc, item) => {
      const additional_task = additional.find((x) => x.id === item);
      return (
        acc +
        Format.round(
          Math.max(
            durations[additional_task.id] * hourlyRate +
              (markups[additional_task.id] ?? 0) -
              (discounts[additional_task.id] ?? 0),
            0,
          ),
        )
      );
    }, 0);
  }
  return amount;
};
const buildWorkSelectionUpdate = ({
  selected,
  selectedWorkId,
  maintenance,
  parts,
  is_fleet_car,
  is_retail_car,
  rob,
  codeSuggestions,
  slaMaintenanceParts,
}) => {
  const buildTaskPartsFromSelectedWork = ({work}) => {
    const nextTaskParts = Object.fromEntries(
      Object.entries(selected.parts).filter(
        ([, value]) =>
          value.source === SOURCES.LKQ || value.source === SOURCES.PartsPoint,
      ),
    );
    const splitParts = [];
    for (const part of parts[work.id] ?? []) {
      if (nextTaskParts[part.itemMpId]) {
        continue;
      }
      if (!is_fleet_car && !is_retail_car && part.rob_code?.length > 0) {
        splitParts.push(part);
        continue;
      }
      nextTaskParts[part.itemMpId] = part;
    }
    return {
      taskParts: nextTaskParts,
      splitParts,
    };
  };
  const mergeSlaMaintenanceParts = (taskParts) => {
    if (slaMaintenanceParts?.length > 0) {
      for (const part of slaMaintenanceParts) {
        taskParts[part.itemMpId] = part;
      }
    }
    return taskParts;
  };
  const work = maintenance
    .flatMap((item) => item.children)
    .find((item) => item.id === selectedWorkId);
  let split_parts = [];
  let rob_code = selected.rob_code;
  let task_parts = {
    ...selected.parts,
  };
  if (work) {
    const {taskParts, splitParts} = buildTaskPartsFromSelectedWork({
      work,
    });
    task_parts = taskParts;
    split_parts = splitParts;
    if (!rob) {
      rob_code = codeSuggestions[work.id];
    }
  }
  task_parts = mergeSlaMaintenanceParts(task_parts);
  return {
    work,
    task_parts,
    split_parts,
    rob_code,
  };
};
const Part = ({
  request_id,
  lessor,
  task_parts,
  sla_parts,
  sla_maintenance_parts,
  onEditArticle,
  hasDiscount,
  partsDiscounts,
  hasMarkup,
  partsMarkups,
  onChangeQuantity,
  onChangeDiscount,
  onChangeMarkup,
}) => (
  <>
    {task_parts.map((item, index) => {
      const sla_part = [
        ...(sla_parts ?? []),
        ...(sla_maintenance_parts ?? []).map((x) => ({
          ...x,
          is_maintenance_part: true,
        })),
      ].find((x) => {
        if (item.gen_art_nr) {
          return x.gen_art_nr === item.gen_art_nr;
        } else if (item.gen_art_nrs) {
          return item.gen_art_nrs.includes(x.gen_art_nr);
        }
        if (x.is_maintenance_part) {
          return item.reference === x.reference;
        }
        return false;
      });
      const itemMpId = item.itemMpId ?? item.attributes.itemMpId;
      const gen_art_nrs = item.gen_art_nr
        ? [Number.parseInt(item.gen_art_nr, 10)]
        : item.gen_arts;
      const quantity = item.quantity ?? item.amounts;
      const description = item.description;
      const price =
        isFinite(item.price) && isFinite(item.quantity)
          ? item.price * item.quantity
          : null;
      const renderArticleText = () => {
        if (sla_part) {
          return (
            <SlaArticleText
              lessor={lessor}
              description={sla_part.description}
              attributes={item.attributes}
              specifications={item.specifications}
              request_id={request_id}
              itemMpId={itemMpId}
            />
          );
        }
        if (isFinite(price)) {
          return <ArticleText source={item.source} number={item.number} />;
        }
        return <>Keuze maken</>;
      };
      return (
        <React.Fragment key={itemMpId}>
          <Pressable
            style={[sy['flex-row']]}
            onPress={() =>
              onEditArticle({
                itemMpId,
                description,
                gen_art_nrs,
                quantity,
                amounts: item.amounts ?? 1,
                task_part: item,
                sla_part: item.attributes?.option_id
                  ? {
                      id: item.id,
                      description: item.description,
                      price: item.price,
                      lessor: lessor,
                    }
                  : sla_part,
              })
            }>
            <View style={[sy['p-4']]}>
              <Quantity
                style={[sy['pr-0']]}
                quantity={quantity}
                onChange={() => onChangeQuantity(item, itemMpId, quantity)}
              />
            </View>
            <View
              style={[
                sy['flex-row'],
                sy['flex-1'],
                sy['justify-between'],
                sy['py-4'],
                sy['pr-4'],
                sy['gap-4'],
              ]}>
              <View style={[sy['flex-1']]}>
                <Text>{description}</Text>
                <Text style={[sy.small, sy['text-lightgray'], sy.underline]}>
                  {renderArticleText()}
                </Text>
              </View>
              <View style={[sy['self-start'], sy['items-end']]}>
                <Pressable
                  disabled={!hasDiscount && !hasMarkup}
                  onPress={() => {
                    if (hasDiscount) {
                      onChangeDiscount(
                        itemMpId,
                        price,
                        partsDiscounts[itemMpId] ?? 0,
                      );
                    }
                    if (hasMarkup) {
                      onChangeMarkup(
                        itemMpId,
                        price,
                        partsMarkups[itemMpId] ?? 0,
                      );
                    }
                  }}>
                  <Text
                    style={[
                      sy.smallRegular,
                      (hasDiscount || hasMarkup) && sy.underline,
                    ]}>
                    {isFinite(price) ? (
                      Format.price(
                        Math.max(
                          price +
                            (partsMarkups[itemMpId] ?? 0) -
                            (partsDiscounts[itemMpId] ?? 0),
                          0,
                        ),
                      )
                    ) : (
                      <></>
                    )}
                  </Text>
                </Pressable>
                <View style={[sy['flex-row'], sy['gap-1']]}>
                  {partsDiscounts[itemMpId] > 0 && (
                    <DiscountIcon fill="#299976" width={18} height={18} />
                  )}
                  {partsMarkups[itemMpId] > 0 && (
                    <SellIcon fill="#FF6C00" width={18} height={18} />
                  )}
                  {item.source === SOURCES.Custom && (
                    <ProductionQuantityLimitsIcon
                      fill="#FF6C00"
                      width={18}
                      height={18}
                    />
                  )}
                </View>
              </View>
            </View>
          </Pressable>
          {index < task_parts.length - 1 && (
            <Divider style={[sy['bg-gray95'], sy['ml-16']]} />
          )}
        </React.Fragment>
      );
    })}
  </>
);
Part.propTypes = {
  request_id: PropTypes.any,
  lessor: PropTypes.any,
  task_parts: PropTypes.any,
  sla_parts: PropTypes.any,
  sla_maintenance_parts: PropTypes.any,
  onEditArticle: PropTypes.any,
  hasDiscount: PropTypes.any,
  partsDiscounts: PropTypes.any,
  hasMarkup: PropTypes.any,
  partsMarkups: PropTypes.any,
  onChangeQuantity: PropTypes.any,
  onChangeDiscount: PropTypes.any,
  onChangeMarkup: PropTypes.any,
};
const History = ({mileage, task_parts, history}) => {
  const item_mp_ids = [
    ...new Set(
      task_parts
        .map((item) => item.attributes.itemMpId ?? null)
        .filter(Boolean)
        .map((item) => Number.parseInt(item, 10)),
    ),
  ];
  const parts = item_mp_ids
    .map((item_mp_id) => history?.[item_mp_id])
    .filter(Boolean)
    // Strip location information
    .flatMap((item) => Object.values(item));
  if (parts.length === 0) {
    return (
      <View style={[sy['p-4'], sy['flex-row']]}>
        <Text>Geen historie bekend</Text>
      </View>
    );
  }
  return parts.map((part, index) => (
    <React.Fragment
      key={`${part.itemMpId ?? part.number ?? 'history'}-${
        part.quantity ?? 0
      }-${index}`}>
      <View style={[sy['p-4'], sy['flex-row']]}>
        <Quantity quantity={part.quantity} />
        <View style={[sy['flex-1'], sy['flex-row'], sy['justify-between']]}>
          <View style={[sy['px-4'], sy['flex-1']]}>
            <Text style={[sy.truncate]}>{part.description}</Text>
            <Text style={[sy.small, sy['text-lightgray']]}>{part.number}</Text>
          </View>
          {<HistoryAge part={part} mileage={mileage} />}
        </View>
      </View>
      {index < parts.length - 1 && (
        <Divider style={[sy['bg-gray95'], sy['ml-18']]} />
      )}
    </React.Fragment>
  ));
};
History.propTypes = {
  mileage: PropTypes.any,
  task_parts: PropTypes.any,
  history: PropTypes.any,
};
const Activity = ActivityRow;
Activity.propTypes = {
  disabled: PropTypes.any,
  rob_code: PropTypes.any,
  description: PropTypes.any,
  onPress: PropTypes.any,
};
const HEADER_HEIGHT_IN_PX = 72;
const CAR_INFO_HEIGHT_IN_PX = 58;
const FOOTER_HEIGHT_IN_PX = 52;
export const getAdditionalAdjustments = (task, additional, field) => ({
  [task.id]: task[field],
  ...Object.fromEntries(
    (additional ?? []).map((item) => [item.id, item[field]]),
  ),
});

export const getDurationDialogContext = (
  dialog,
  selectedId,
  priceCheck,
  source,
) =>
  dialog.task_id === selectedId
    ? {priceCheck, work_source: source}
    : {priceCheck: null, work_source: null};

const Maintenance = ({
  onDismiss,
  dimensions,
  license,
  is_fleet_car,
  is_retail_car,
  vin_number,
  mileage,
  car,
  request_id,
  damage_id,
  dealer_id,
  company_id,
  lessor,
  task,
  additional,
  rob,
  component,
  onChange,
  onShowDenialReasons,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [screen, setScreen] = useState(SCREENS.TASK);
  const [hasMaintenance, setHasMaintenance] = useState(false);
  const [maintenance, setMaintenance] = useState([]);
  const [source, setSource] = useState(null);
  const [parts, setParts] = useState(null);
  const [codes, setCodes] = useState(null);
  const [codeSuggestions, setCodeSuggestions] = useState(null);
  const initial_parts = task.parts ?? {};
  const [selected, setSelected] = useState({
    ...task,
    parts: initial_parts,
  });
  const [discounts, setDiscounts] = useState(
    getAdditionalAdjustments(task, additional, 'discount'),
  );
  const [partsDiscounts, setPartsDiscounts] = useState(
    Object.keys(initial_parts).reduce((acc, itemMpId) => {
      acc[itemMpId] = initial_parts[itemMpId]?.discount ?? 0;
      return acc;
    }, {}),
  );
  const [markups, setMarkups] = useState(
    getAdditionalAdjustments(task, additional, 'markup'),
  );
  const [partsMarkups, setPartsMarkups] = useState(
    Object.keys(initial_parts).reduce((acc, itemMpId) => {
      acc[itemMpId] = initial_parts[itemMpId]?.markup ?? 0;
      return acc;
    }, {}),
  );
  const [selectedAdditional, setSelectedAdditional] = useState(
    additional?.map((item) => item.id) ?? [],
  );
  const [selectedWorkId, setSelectedWorkId] = useState(task.reference);
  const [robComponents, setRobComponents] = useState(null);
  const [history, setHistory] = useState(null);
  const [hasHistory, setHasHistory] = useState(null);
  const [hourlyRate, setHourlyRate] = useState(null);
  const [slaParts, setSlaParts] = useState(null);
  const [slaMaintenanceParts, setSlaMaintenanceParts] = useState(null);
  const [editArticle, setEditArticle] = useState(null);
  const [taskAmount, setTaskAmount] = useState(task.task_amount);
  const [priceCheck, setPriceCheck] = useState(null);
  const [hasDiscount, setHasDiscount] = useState(
    task.discount > 0 ||
      additional?.some((item) => item.discount > 0) ||
      Object.values(initial_parts)
        .filter(Boolean)
        .some((item) => item.discount > 0),
  );
  const [hasMarkup, setHasMarkup] = useState(
    task.markup > 0 ||
      additional?.some((item) => item.markup > 0) ||
      Object.values(initial_parts)
        .filter(Boolean)
        .some((item) => item.markup > 0),
  );
  const [componentsWithDamageAllowed, setComponentsWithDamageAllowed] =
    useState(true);
  const initialAdditionalDurations =
    additional?.reduce((acc, item) => {
      acc[item.id] = item.duration;
      return acc;
    }, {}) ?? {};
  const [durations, setDurations] = useState({
    [task.id]: task.duration,
    ...initialAdditionalDurations,
  });
  const initialAdditionalOriginalDurations =
    additional?.reduce((acc, item) => {
      acc[item.id] = item.original_duration;
      return acc;
    }, {}) ?? {};
  const [originalDurations, setOriginalDurations] = useState({
    [task.id]: task.original_duration,
    ...initialAdditionalOriginalDurations,
  });
  const toggleHasDiscount = () => {
    setHasDiscount((prevState) => {
      const newState = !prevState;
      if (newState) {
        // Reset markup
        setMarkups({});
        setPartsMarkups({});
      } else {
        setDiscounts({});
        setPartsDiscounts({});
      }
      return newState;
    });
  };
  const toggleHasMarkup = () => {
    setHasMarkup((prevState) => {
      const newState = !prevState;
      if (newState) {
        // Reset discount
        setDiscounts({});
        setPartsDiscounts({});
      } else {
        setMarkups({});
        setPartsMarkups({});
      }
      return newState;
    });
  };
  const [showEditQuantity, setShowEditQuantity] = useState(null);
  const [showPartsHistory, setShowPartsHistory] = useState(false);
  const [showLeniencyActionsheet, setShowLeniencyActionsheet] = useState(false);
  const [showWarrantyInfoDialog, setShowWarrantyInfoDialog] = useState(false);
  const [showAddPartActionsheet, setShowAddPartActionsheet] = useState(false);
  const [showDiscountDialog, setShowDiscountDialog] = useState(null);
  const [showMarkupDialog, setShowMarkupDialog] = useState(null);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(null);
  const [showPartsSplitDialog, setShowPartsSplitDialog] = useState(false);
  const [showDamageNotAllowedDialog, setShowDamageNotAllowedDialog] =
    useState(false);
  const [showDurationDialog, setShowDurationDialog] = useState(null);
  const user_settings = useSelector((state) => state.user_settings);
  useEffect(() => {
    const fetchMaintenance = async () => {
      const {result} = await serviceRequestApi.has_maintenance(request_id);
      setHasMaintenance(result?.has_maintenance);
      if (result.has_maintenance) {
        const {result} = await serviceRequestApi.maintenance(request_id);
        setMaintenance(normalizeMaintenanceItems(result.maintenance));
        setParts(result.parts);
        setCodes(result.codes);
        setCodeSuggestions(result.suggestions);
        setSource(result.source);
      }
      if (!task?.id) {
        if (result?.has_maintenance) {
          setScreen(SCREENS.WORK);
        } else {
          setSelected({
            ...task,
            parts: initial_parts,
            duration: 0,
          });
          setScreen(SCREENS.TASK);
        }
      }
    };
    const fetchRobComponents = async () => {
      const {result: robComponentsResult} = await robApi.components(
        license,
        request_id,
      );
      setRobComponents(robComponentsResult);
    };
    const fetchRobContract = async () => {
      const {result: rob_contract} = await robApi.contract(license, request_id);
      setComponentsWithDamageAllowed(
        rob_contract?.supplierContract.repairAndMaintenance
          .componentsWithDamageAllowed,
      );
    };
    const fetchHistory = async () => {
      const response = await serviceRequestApi.car_history(request_id);
      const {history} = response.result;
      setHistory(history);
    };
    const fetchSla = async () => {
      const {result: sla_result} =
        (await serviceRequestApi.sla(request_id)) ?? {};
      setSlaParts(sla_result?.parts);
      setSlaMaintenanceParts(sla_result?.maintenance_parts);
    };
    const fetch = async () => {
      setLoading(true);

      // The car history only feeds the parts history, which renders once it
      // arrives, so it does not hold up the sheet. The other calls are
      // independent of each other and run side by side.
      fetchHistory().catch(trace);
      await Promise.all([
        fetchMaintenance(),
        ...(!is_fleet_car && !is_retail_car
          ? [fetchRobComponents(), fetchRobContract()]
          : []),
        fetchSla(),
      ]);
      setLoading(false);
    };
    fetch().catch(trace);
  }, [
    request_id,
    license,
    task,
    rob,
    vin_number,
    lessor,
    is_fleet_car,
    is_retail_car,
  ]);
  useEffect(() => {
    const fetch = async () => {
      const {result} = await serviceRequestApi.sla_hourly_rate(
        request_id,
        selected.rob_code,
      );
      setHourlyRate(result.hourly_rate);
    };
    fetch().catch(trace);
  }, [request_id, selected?.rob_code]);
  useEffect(() => {
    const fetch = async () => {
      const {result: price_check} = await robApi.task_price_check(
        company_id,
        lessor,
        selected.rob_code,
        ROB_OPERATION_UITVOEREN,
        vin_number,
        hourlyRate,
        request_id,
        license,
      );
      setPriceCheck(price_check);
    };
    if (selected?.rob_code && hourlyRate) {
      fetch().catch(trace);
    }
  }, [
    selected?.rob_code,
    hourlyRate,
    vin_number,
    lessor,
    company_id,
    request_id,
    license,
  ]);

  // Move the SLA option part to the API in a future backend iteration.
  useEffect(() => {
    setSelected((prevState) => {
      const task_parts = prevState.parts;
      let changed = false;
      for (const task_part of Object.values(task_parts)) {
        if (task_part?.id || !task_part?.gen_arts?.length) {
          continue;
        }
        const sla_part = slaParts?.find((item) =>
          task_part.gen_arts.includes(Number(item.gen_art_nr)),
        );
        if (sla_part) {
          changed = true;
          task_parts[task_part.itemMpId] = {
            ...sla_part,
            description: sla_part.designation,
            quantity: task_part.amounts,
            attributes: {
              itemMpId: task_part.itemMpId,
            },
            specifications: task_part.specifications,
          };

          // SLA part options
          for (const option of sla_part.options ?? []) {
            if (
              option.condition?.rob_operation_code.includes(
                ROB_OPERATION_UITVOEREN,
              )
            ) {
              task_parts[option.id] = {
                gen_art_nr: option.gen_art_nr,
                description: option.item,
                price: option.price,
                quantity: 1,
                attributes: {
                  itemMpId: option.id,
                  option_id: option.id,
                },
              };
            }
          }
        }
      }
      if (changed) {
        return {
          ...prevState,
          parts: {
            ...task_parts,
          },
        };
      }
      return prevState;
    });
  }, [selected, slaParts]);
  useEffect(() => {
    if (!history) {
      return;
    }
    const item_mp_ids = new Set(
      [
        ...(parts?.[selected.reference]?.map((item) => item.itemMpId) ?? []),
        ...Object.values(selected.parts).map(
          (item) => item.attributes?.itemMpId,
        ),
      ].map(Number),
    );
    setHasHistory(
      Object.keys(history).some((item_mp_id) =>
        item_mp_ids.has(Number(item_mp_id)),
      ),
    );
  }, [selected, history, parts]);
  useEffect(() => {
    const amount = calculateMaintenanceAmount({
      selected,
      selectedAdditional,
      additional,
      hourlyRate,
      durations,
      markups,
      discounts,
      partsMarkups,
      partsDiscounts,
    });
    setTaskAmount(amount);
  }, [
    selected,
    selectedAdditional,
    hourlyRate,
    additional,
    durations,
    discounts,
    partsDiscounts,
    task,
    markups,
    partsMarkups,
  ]);
  const onAddParts = (parts) => {
    const changes =
      parts?.reduce((acc, {id, ...item}) => {
        acc[id] = {
          ...item,
        };
        return acc;
      }, {}) ?? {};
    setSelected({
      ...selected,
      parts: {
        ...selected.parts,
        ...changes,
      },
    });
    setShowAddPartActionsheet(false);
  };
  const retrieveChanges = () => {
    let work_id = selected.reference;
    let work_number = selected.number;
    let work_text = selected.work_text;
    let duration = durations[selected.id];
    let original_duration = originalDurations[selected.id];
    let itemMpId = selected.itemMpId;
    let original_work_time = selected.reference
      ? selected.original_work_time
      : null;
    let work_source;
    let work = maintenance
      .flatMap((item) => item.children)
      .find((child) => child.id === selected.reference);
    if (work) {
      work_id = work.id;
      work_number = work.number;
      work_text = work.text;
      itemMpId = work.itemMpId;
      original_work_time = work.work_time;
      work_source = source;
    }
    const changes = {
      duration,
      original_duration,
      work_id,
      work_number,
      work_text,
      work_item_mp_id: itemMpId,
      source: work_source,
      original_work_time,
      rob_code: selected.rob_code,
      description: robComponents?.[selected.rob_code]?.description,
      parts: Object.keys(selected.parts).map((itemMpId) => {
        const item = selected.parts[itemMpId];
        const quantity = item.quantity ?? item.amounts ?? 1;
        const gen_art_nr = item.gen_art_nr ?? item.gen_arts?.[0];
        return {
          ...item,
          discount: Math.min(
            partsDiscounts[itemMpId] ?? 0,
            item.price * quantity,
          ),
          markup: partsMarkups[itemMpId] ?? 0,
          quantity,
          gen_art_nr,
          attributes: {
            ...item.attributes,
            itemMpId,
          },
        };
      }),
      split_parts: work_id ? selected.split_parts : null,
      operation: ROB_OPERATION_UITVOEREN,
      reason: ROB_REASON_NVT,
      warranty: selected.warranty,
      leniency_amount: isRobLeniencyTask(selected.warranty)
        ? selected.leniency_amount
        : null,
      is_damage: selected.is_damage,
      discount: discounts?.[task.id],
      markup: markups?.[task.id],
      additional: selectedAdditional.map((id) => ({
        id,
        discount: discounts[id],
        markup: markups[id],
        duration: durations[id],
        original_duration: originalDurations[id],
      })),
    };
    return changes;
  };
  const onOK = () => {
    const changes = retrieveChanges();
    onChange({
      ...changes,
      task_id: task.id,
    });
  };
  const onDuplicate = async () => {
    const changes = retrieveChanges();
    await onChange({
      ...changes,
      replace_id: rob.id,
      rob_code: showDuplicateWarning.change,
      parts: changes.parts?.map((part) => ({
        ...part,
        id: null,
      })),
    });
  };
  const onResetParts = () => {
    for (const part of parts[selectedWorkId] ?? []) {
      if (!selected.parts[part.itemMpId]) {
        let quantity = part.amounts;
        let description = part.description;
        const sla_part = slaParts.find((item) =>
          part.gen_arts.includes(Number.parseInt(item.gen_art_nr, 10)),
        );
        let price;
        let gen_art_nr = part.gen_arts[0];
        if (sla_part) {
          description = sla_part.description;
          gen_art_nr = sla_part.gen_art_nr;
          price = sla_part.price;
        }
        selected.parts[part.itemMpId] = {
          description,
          quantity,
          price,
          gen_art_nr,
          attributes: {
            itemMpId: part.itemMpId,
          },
        };
      }
    }
    setSelected({
      ...selected,
      parts: {
        ...selected.parts,
      },
    });
  };
  if (showAddPartActionsheet) {
    return (
      <PartSelection
        request_id={request_id}
        dealer_id={dealer_id}
        license={license}
        parts={selected.parts}
        lessor={lessor}
        rob_code={selected.rob_code}
        onChange={onAddParts}
        onDismiss={() => setShowAddPartActionsheet(false)}
      />
    );
  }
  if (editArticle) {
    return (
      <AddArticleActionsheet
        dismissable={false}
        onDismiss={() => setEditArticle(null)}
        request_id={request_id}
        license={license}
        dealer_id={dealer_id}
        description={editArticle.description}
        gen_arts={editArticle.gen_art_nrs}
        item_mp_id={editArticle.itemMpId}
        number={editArticle.task_part?.number}
        reference={editArticle.task_part?.reference}
        sla_part={editArticle.sla_part}
        task_part={editArticle.task_part}
        lessor={lessor}
        rob_code={selected.rob_code}
        onChange={({
          id,
          reference,
          number,
          source,
          description,
          brand,
          price,
          margin,
          quantity,
          gen_art_nr,
          specifications,
        }) => {
          selected.parts[editArticle.itemMpId] = updateExistingPart(
            selected.parts[editArticle.itemMpId],
            editArticle,
            {
              id,
              reference,
              number,
              source,
              description,
              brand,
              price,
              margin,
              quantity,
              gen_art_nr,
              specifications,
            },
          );
          setSelected({
            ...selected,
            parts: {
              ...selected.parts,
            },
          });
          setEditArticle(null);
        }}
        onRemove={() => {
          selected.parts[editArticle.itemMpId] = null;
          setSelected({
            ...selected,
            parts: {
              ...pickBy(selected.parts),
            },
          });
          setEditArticle(null);
        }}
      />
    );
  }
  if (showLeniencyActionsheet) {
    return (
      <LeniencyActionsheet
        onDismiss={() => setShowLeniencyActionsheet(false)}
        value={selected.warranty}
        task_amount={taskAmount}
        onChange={(leniency, leniency_amount) => {
          setSelected({
            ...selected,
            warranty: leniency,
            leniency_amount,
            is_damage: false,
          });
          setShowLeniencyActionsheet(false);
        }}
      />
    );
  }
  if (screen === SCREENS.ROB_CODE) {
    return (
      <RobCodeActionsheet
        request_id={request_id}
        license={license}
        codes={[
          ...new Set([
            selected?.rob_code,
            ...(codes?.[selected?.reference || null] ?? []),
          ]),
        ]
          .filter(Boolean)
          .slice(0, 8)}
        rob_code={selected?.rob_code}
        options={{
          maintenance_codes_only: true,
        }}
        onDismiss={() => setScreen(SCREENS.TASK)}
        onChange={(rob_code) => {
          if (
            isRobTaskDenied(rob?.status.code) ||
            isRobTaskChanged(rob?.status.code)
          ) {
            setShowDuplicateWarning({
              rob_code: selected?.rob_code,
              change: rob_code,
            });
          } else {
            setSelected({
              ...selected,
              rob_code,
            });
          }
          setScreen(SCREENS.TASK);
        }}
      />
    );
  }
  return (
    <>
      <Actionsheet visible={true} onDismiss={onDismiss}>
        {screen === SCREENS.TASK && (
          <>
            <Title>
              <View
                style={[
                  sy['flex-row'],
                  sy['justify-between'],
                  sy['items-center'],
                  sy['flex-1'],
                  {
                    width: '100%',
                  },
                ]}>
                <Text
                  style={[
                    sy.largePlus,
                    {
                      lineHeight: 30,
                    },
                  ]}>
                  Onderhoud
                </Text>
                <View style={[sy['flex-row'], sy['items-center']]}>
                  {!rob && !!priceCheck && (
                    <PriceCheck
                      rob_code={
                        is_fleet_car || is_retail_car ? null : selected.rob_code
                      }
                      amount={taskAmount}
                      range={priceCheck?.range}
                      duration={durations[selected.id]}
                      suggested_duration={priceCheck?.suggested_duration}
                      onChangeDuration={(suggested) => {
                        setDurations((prevState) => ({
                          ...prevState,
                          [selected.id]: suggested,
                        }));
                        setOriginalDurations((prevState) => ({
                          ...prevState,
                          [selected.id]: suggested,
                        }));
                      }}
                    />
                  )}
                  {rob?.status.code && (
                    <Pressable
                      style={[sy['flex-row']]}
                      disabled={is_retail_car || is_fleet_car}
                      onPress={() => onShowDenialReasons()}>
                      <RobTaskIcon
                        statuses={[rob?.status.code]}
                        height="24"
                        width="24"
                      />
                    </Pressable>
                  )}
                </View>
              </View>
            </Title>

            {loading && (
              <>
                <Divider style={sy['bg-gray88']} />
                <View style={[sy['p-4']]}>
                  <ActivityIndicator size="large" color="#231fda" />
                </View>
              </>
            )}
            {!loading && (
              <>
                <Content
                  style={{
                    paddingBottom: 0,
                    paddingHorizontal: 0,
                  }}>
                  <ScrollView
                    contentContainerStyle={{
                      maxHeight:
                        dimensions.window.height -
                        HEADER_HEIGHT_IN_PX -
                        FOOTER_HEIGHT_IN_PX,
                    }}>
                    <Grouping
                      header="Arbeid"
                      initial={{
                        expand: true,
                      }}>
                      <View style={[sy['p-4'], sy['gap-4']]}>
                        <View style={[sy['flex-row'], sy['gap-4']]}>
                          <View style={[sy['justify-center']]}>
                            {selected.reference ? (
                              <FactoryOutlinedIcon />
                            ) : (
                              <EditOutlinedIcon />
                            )}
                          </View>
                          <View style={[sy['flex-1']]}>
                            <View
                              style={[
                                sy['flex-row'],
                                sy['justify-between'],
                                sy['gap-4'],
                              ]}>
                              <Text>
                                {selected.reference ? (
                                  selected.work_text
                                ) : (
                                  <>Handmatige invoer</>
                                )}
                              </Text>
                              <View
                                style={[
                                  sy['flex-row'],
                                  sy['gap-2'],
                                  sy['justify-end'],
                                ]}>
                                {(() => {
                                  if (markups[task.id] > 0) {
                                    return (
                                      <SellIcon
                                        fill="#FF6C00"
                                        width={18}
                                        height={18}
                                      />
                                    );
                                  }
                                  if (discounts[task.id] > 0) {
                                    return (
                                      <DiscountIcon
                                        fill="#299976"
                                        width={18}
                                        height={18}
                                      />
                                    );
                                  }
                                  return null;
                                })()}
                                {isFinite(durations[selected.id]) && (
                                  <Pressable
                                    disabled={!hasDiscount && !hasMarkup}
                                    onPress={() => {
                                      if (hasDiscount) {
                                        setShowDiscountDialog({
                                          discount: discounts[task.id],
                                          amount:
                                            durations[selected.id] * hourlyRate,
                                          onChange: (discount) => {
                                            setDiscounts({
                                              ...discounts,
                                              [task.id]: discount,
                                            });
                                          },
                                        });
                                      }
                                      if (hasMarkup) {
                                        setShowMarkupDialog({
                                          markup: markups[task.id],
                                          amount:
                                            durations[selected.id] * hourlyRate,
                                          onChange: (markup) => {
                                            setMarkups({
                                              ...markups,
                                              [task.id]: markup,
                                            });
                                          },
                                        });
                                      }
                                    }}>
                                    <Text
                                      style={[
                                        sy.smallRegular,
                                        (hasDiscount || hasMarkup) &&
                                          sy.underline,
                                      ]}>
                                      {Format.price(
                                        Math.max(
                                          durations[selected.id] * hourlyRate +
                                            (markups[task.id] ?? 0) -
                                            (discounts[task.id] ?? 0),
                                          0,
                                        ),
                                      )}
                                    </Text>
                                  </Pressable>
                                )}
                              </View>
                            </View>
                            <View
                              style={[sy['flex-row'], sy['justify-between']]}>
                              <Pressable
                                onPress={() => setScreen(SCREENS.WORK)}>
                                <Text
                                  style={[
                                    sy.underline,
                                    sy.small,
                                    sy['text-lightgray'],
                                  ]}>
                                  <>
                                    {selected.reference ? (
                                      <>{source ?? 'TecRMI'}</>
                                    ) : (
                                      <>Invoer</>
                                    )}
                                  </>
                                </Text>
                              </Pressable>
                              <View
                                style={[
                                  sy['flex-row'],
                                  sy['gap-2'],
                                  sy['justify-end'],
                                ]}>
                                <DurationIcon
                                  duration={durations[selected.id]}
                                  original_duration={
                                    originalDurations[selected.id]
                                  }
                                  original_work_time={
                                    selected.original_work_time
                                  }
                                  calculated_work_time={
                                    selected.calculated_work_time
                                  }
                                />
                                <Pressable
                                  style={[sy['flex-row'], sy['gap-2']]}
                                  onPress={() =>
                                    setShowDurationDialog({
                                      task_id: selected.id,
                                      original_work_time:
                                        selected.original_work_time,
                                      calculated_work_time:
                                        selected.calculated_work_time,
                                    })
                                  }>
                                  {isFinite(durations[selected.id]) ? (
                                    <DurationText
                                      duration={durations[selected.id]}
                                      original_duration={
                                        originalDurations[selected.id]
                                      }
                                      original_work_time={
                                        selected.original_work_time
                                      }
                                      calculated_work_time={
                                        selected.calculated_work_time
                                      }
                                    />
                                  ) : (
                                    <Text
                                      style={[
                                        sy.smallCondensedRegular,
                                        sy['text-lightgray'],
                                        sy.underline,
                                      ]}>
                                      Keuze maken
                                    </Text>
                                  )}
                                </Pressable>
                              </View>
                            </View>
                          </View>
                        </View>
                        {additional
                          ?.sort((a, b) =>
                            (a.work_text ?? a.description).localeCompare(
                              b.work_text ?? b.description,
                            ),
                          )
                          .map((item) => (
                            <React.Fragment key={item.id}>
                              <Divider style={[sy['bg-gray95']]} />
                              <View style={[sy['flex-row'], sy['gap-4']]}>
                                <View style={[sy['justify-center']]}>
                                  <Checkbox
                                    checked={selectedAdditional.includes(
                                      item.id,
                                    )}
                                    disabledTimeout={0}
                                    onPress={() => {
                                      if (
                                        selectedAdditional.includes(item.id)
                                      ) {
                                        setSelectedAdditional(
                                          selectedAdditional.filter(
                                            (x) => x !== item.id,
                                          ),
                                        );
                                      } else {
                                        setSelectedAdditional([
                                          ...selectedAdditional,
                                          item.id,
                                        ]);
                                      }
                                    }}
                                  />
                                </View>
                                <View style={[sy['flex-1']]}>
                                  <View
                                    style={[
                                      sy['flex-row'],
                                      sy['justify-between'],
                                      sy['gap-4'],
                                    ]}
                                    onPress={() => setScreen(SCREENS.WORK)}>
                                    <Text>
                                      {item.work_text ?? item.description}
                                    </Text>
                                    <View
                                      style={[
                                        sy['flex-row'],
                                        sy['gap-2'],
                                        sy['justify-end'],
                                      ]}>
                                      {(() => {
                                        if (markups[item.id] > 0) {
                                          return (
                                            <SellIcon
                                              fill="#FF6C00"
                                              width={18}
                                              height={18}
                                            />
                                          );
                                        }
                                        if (discounts[item.id] > 0) {
                                          return (
                                            <DiscountIcon
                                              fill="#299976"
                                              width={18}
                                              height={18}
                                            />
                                          );
                                        }
                                        return null;
                                      })()}
                                      <Pressable
                                        disabled={!hasDiscount && !hasMarkup}
                                        onPress={() => {
                                          if (hasDiscount) {
                                            setShowDiscountDialog({
                                              discount: discounts[item.id],
                                              amount:
                                                durations[item.id] * hourlyRate,
                                              onChange: (discount) => {
                                                setDiscounts({
                                                  ...discounts,
                                                  [item.id]: discount,
                                                });
                                              },
                                            });
                                          }
                                          if (hasMarkup) {
                                            setShowMarkupDialog({
                                              markup: markups[item.id],
                                              amount:
                                                durations[item.id] * hourlyRate,
                                              onChange: (markup) => {
                                                setMarkups({
                                                  ...markups,
                                                  [item.id]: markup,
                                                });
                                              },
                                            });
                                          }
                                        }}>
                                        <Text
                                          style={[
                                            sy.smallCondensedRegular,
                                            (hasDiscount || hasMarkup) &&
                                              sy.underline,
                                          ]}>
                                          {Format.price(
                                            Math.max(
                                              durations[item.id] * hourlyRate -
                                                (markups[item.id] ?? 0) -
                                                (discounts[item.id] ?? 0),
                                              0,
                                            ),
                                          )}
                                        </Text>
                                      </Pressable>
                                    </View>
                                  </View>
                                  <View
                                    style={[
                                      sy['flex-row'],
                                      sy['justify-between'],
                                    ]}>
                                    <Text
                                      style={[sy.small, sy['text-lightgray']]}>
                                      TecRMI
                                    </Text>

                                    <View style={[sy['flex-row'], sy['gap-2']]}>
                                      <DurationIcon
                                        duration={durations[item.id]}
                                        original_duration={
                                          originalDurations[item.id]
                                        }
                                        original_work_time={
                                          item.original_work_time
                                        }
                                        calculated_work_time={
                                          item.calculated_work_time
                                        }
                                      />
                                      <Pressable
                                        style={[sy['flex-row'], sy['gap-2']]}
                                        onPress={() =>
                                          setShowDurationDialog({
                                            task_id: item.id,
                                            original_work_time:
                                              item.original_work_time,
                                            calculated_work_time:
                                              item.calculated_work_time,
                                          })
                                        }>
                                        <DurationText
                                          duration={durations[item.id]}
                                          original_duration={
                                            originalDurations[item.id]
                                          }
                                          original_work_time={
                                            item.original_work_time
                                          }
                                          calculated_work_time={
                                            item.calculated_work_time
                                          }
                                        />
                                      </Pressable>
                                    </View>
                                  </View>
                                </View>
                              </View>
                            </React.Fragment>
                          ))}
                      </View>
                    </Grouping>
                    {isFinite(durations[selected.id]) && (
                      <>
                        <Divider style={sy['bg-gray88']} />
                        <View
                          style={[
                            sy['p-4'],
                            sy['flex-row'],
                            sy['justify-between'],
                          ]}>
                          <Text style={sy.mediumPlus}>Artikel(en)</Text>
                          {Object.values(selected.parts).length > 0 && (
                            <Pressable
                              onPress={() =>
                                setShowPartsHistory(!showPartsHistory)
                              }>
                              {hasHistory && (
                                <>
                                  {showPartsHistory ? (
                                    <HistoryToggleOffIcon />
                                  ) : (
                                    <HistoryIcon />
                                  )}
                                </>
                              )}
                            </Pressable>
                          )}
                        </View>
                        {Object.values(selected.parts).length > 0 ? (
                          <>
                            <Divider style={sy['bg-gray88']} />
                            <>
                              {hasHistory && showPartsHistory ? (
                                <History
                                  mileage={mileage}
                                  task_parts={Object.values(selected.parts)}
                                  history={history}
                                />
                              ) : (
                                <Part
                                  request_id={request_id}
                                  lessor={lessor}
                                  task_parts={Object.values(selected.parts)}
                                  sla_parts={slaParts}
                                  sla_maintenance_parts={slaMaintenanceParts}
                                  onEditArticle={setEditArticle}
                                  hasDiscount={hasDiscount}
                                  partsDiscounts={partsDiscounts}
                                  hasMarkup={hasMarkup}
                                  partsMarkups={partsMarkups}
                                  onChangeQuantity={(
                                    task_part,
                                    itemMpId,
                                    amounts,
                                  ) => {
                                    setShowEditQuantity({
                                      initialValue:
                                        task_part?.quantity ?? amounts,
                                      onChange: (value) => {
                                        const changes = selected.parts;
                                        if (value > 0) {
                                          changes[itemMpId] = {
                                            ...changes[itemMpId],
                                            quantity: value,
                                          };
                                        } else {
                                          delete changes[itemMpId];
                                        }
                                        setSelected({
                                          ...selected,
                                          parts: {
                                            ...changes,
                                          },
                                        });
                                      },
                                    });
                                  }}
                                  onChangeDiscount={(
                                    itemMpId,
                                    amount,
                                    discount,
                                  ) => {
                                    setShowDiscountDialog({
                                      discount,
                                      amount,
                                      onChange: (value) => {
                                        setPartsDiscounts({
                                          ...partsDiscounts,
                                          [itemMpId]: value,
                                        });
                                      },
                                    });
                                  }}
                                  onChangeMarkup={(
                                    itemMpId,
                                    amount,
                                    markup,
                                  ) => {
                                    setShowMarkupDialog({
                                      markup,
                                      amount,
                                      onChange: (value) => {
                                        setPartsMarkups({
                                          ...partsMarkups,
                                          [itemMpId]: value,
                                        });
                                      },
                                    });
                                  }}
                                />
                              )}
                            </>
                            <Divider style={[sy['bg-gray95'], sy['ml-16']]} />
                          </>
                        ) : (
                          <Divider style={sy['bg-gray88']} />
                        )}
                        <View style={[sy['flex-row']]}>
                          <Pressable
                            style={[sy['flex-row'], sy['flex-1']]}
                            onPress={() => setShowAddPartActionsheet(true)}>
                            <View style={[sy['p-4']]}>
                              <AddIcon fill="#231fda" />
                            </View>
                            <View style={[sy['flex-1']]}>
                              <View style={[sy['p-4'], sy['pl-2']]}>
                                <Text>Artikel toevoegen</Text>
                              </View>
                            </View>
                          </Pressable>
                          {parts?.[selectedWorkId]?.some(
                            (part) => !selected.parts[part.itemMpId],
                          ) && (
                            <Pressable
                              style={[sy['p-4']]}
                              onPress={onResetParts}>
                              <RestartIcon width={24} height={24} />
                            </Pressable>
                          )}
                        </View>
                        {robComponents && (
                          <Grouping
                            header="ROB-Net"
                            initial={{
                              expand: true,
                            }}>
                            <Activity
                              disabled={
                                rob?.id &&
                                !isRobTaskDenied(rob?.status.code) &&
                                !isRobTaskChanged(rob?.status.code)
                              }
                              rob_code={selected.rob_code}
                              description={
                                robComponents[selected?.rob_code]?.description
                              }
                              onPress={() => {
                                setScreen(SCREENS.ROB_CODE);
                              }}
                            />
                          </Grouping>
                        )}
                      </>
                    )}
                  </ScrollView>
                </Content>
                <Divider style={sy['bg-gray88']} />
                <Actions style={[sy['justify-between']]}>
                  <View style={[sy['flex-row'], sy['items-center']]}>
                    <Text style={[sy.mediumBold, sy['pl-2'], sy['pr-4']]}>
                      {Format.price(taskAmount || 0)}
                    </Text>
                    <ActivityOptions
                      selected={selected}
                      setSelected={setSelected}
                      hasDiscount={hasDiscount}
                      hasMarkup={hasMarkup}
                      toggleHasDiscount={toggleHasDiscount}
                      toggleHasMarkup={toggleHasMarkup}
                      componentsWithDamageAllowed={componentsWithDamageAllowed}
                      setShowWarrantyInfoDialog={setShowWarrantyInfoDialog}
                      setShowLeniencyActionsheet={setShowLeniencyActionsheet}
                      setShowDamageNotAllowedDialog={
                        setShowDamageNotAllowedDialog
                      }
                    />
                  </View>
                  <View style={[sy['flex-row'], sy['gap-2']]}>
                    <ActionButton onPress={onDismiss}>Annuleren</ActionButton>
                    <ActionButton onPress={onOK}>OK</ActionButton>
                  </View>
                </Actions>
              </>
            )}
          </>
        )}
        {screen === SCREENS.WORK && (
          <>
            <Title>
              <View
                style={[
                  sy['flex-row'],
                  sy['justify-between'],
                  sy['items-center'],
                  sy['flex-1'],
                  {
                    width: '100%',
                  },
                ]}>
                <Text
                  style={[
                    sy.largePlus,
                    {
                      lineHeight: 30,
                    },
                  ]}>
                  Standaard service
                </Text>
              </View>
            </Title>
            <Divider style={sy['bg-gray88']} />

            {loading && (
              <View style={[sy['p-4']]}>
                <ActivityIndicator size="large" color="#231fda" />
              </View>
            )}
            {!loading && (
              <>
                <Content
                  style={{
                    paddingBottom: 0,
                    paddingHorizontal: 0,
                  }}>
                  <View>
                    <View
                      style={[sy['flex-row'], sy['items-center'], sy['p-4']]}>
                      <View style={[sy['pr-4']]}>
                        <DirectionsCarIcon />
                      </View>
                      <View style={[sy['flex-1'], sy['flex-row']]}>
                        {isFinite(mileage) && (
                          <Text style={[sy.mediumBold]}>
                            {Format.number(mileage, 0, true)}
                            &nbsp;km&nbsp;&middot;&nbsp;
                          </Text>
                        )}
                        <Text style={[sy.mediumBold]}>
                          {Format.number(
                            moment().diff(car.entry_date, 'months') / 12,
                            1,
                            true,
                            {
                              minimumFractionDigits: 0,
                            },
                          )}
                          &nbsp;jaar
                        </Text>
                      </View>
                    </View>
                    <Divider style={sy['bg-gray88']} />
                    <ScrollView
                      contentContainerStyle={{
                        maxHeight:
                          dimensions.window.height -
                          (HEADER_HEIGHT_IN_PX + CAR_INFO_HEIGHT_IN_PX) -
                          FOOTER_HEIGHT_IN_PX,
                      }}>
                      {hasMaintenance && maintenance && (
                        <>
                          <View
                            style={[
                              sy['flex-row'],
                              sy['items-center'],
                              sy['p-4'],
                            ]}>
                            <View style={[sy['pr-4']]}>
                              <RadioButton
                                checked={!!selectedWorkId}
                                onPress={() => {
                                  if (!selectedWorkId) {
                                    setSelectedWorkId(
                                      maintenance[0]?.children[0].id,
                                    );
                                  }
                                }}
                              />
                            </View>
                            <View style={[sy['flex-row'], sy['flex-1']]}>
                              <Text style={[sy['flex-1']]}>
                                {source ?? 'TecRMI'} onderhoud
                              </Text>
                              <View>
                                <FactoryOutlinedIcon />
                              </View>
                            </View>
                          </View>
                          <View style={[sy['pl-4']]}>
                            {Object.values(maintenance).map((item) => {
                              const work_selected = item.children
                                .map((item) => item.id)
                                .includes(selectedWorkId);
                              return (
                                <React.Fragment key={item.id}>
                                  <View
                                    style={[
                                      sy['flex-row'],
                                      sy['items-start'],
                                      sy['p-4'],
                                      sy['pl-6'],
                                    ]}>
                                    <View style={[sy['pr-4']]}>
                                      <RadioButton
                                        checked={work_selected}
                                        onPress={() => {
                                          setSelectedWorkId(
                                            item.children[0].id,
                                          );
                                        }}
                                      />
                                    </View>
                                    <View style={[sy['flex-1']]}>
                                      <Text>
                                        {item.text}
                                        {item.children.length === 1 &&
                                          parts[item.children[0].id] && (
                                            <>
                                              &nbsp;(
                                              {
                                                parts[item.children[0].id]
                                                  .length
                                              }
                                              )
                                            </>
                                          )}
                                      </Text>
                                      {item.qualifiers.length > 0 && (
                                        <Text
                                          style={[
                                            sy.smallRegular,
                                            sy['text-lightgray'],
                                          ]}>
                                          {item.qualifiers
                                            .map((q) => q.text)
                                            .join(', ')}
                                        </Text>
                                      )}
                                    </View>
                                    {item.children.length === 1 && (
                                      <Text>
                                        {Format.number(
                                          item.children[0].work_time,
                                          2,
                                        )}
                                        &nbsp;u
                                      </Text>
                                    )}
                                  </View>
                                  {work_selected &&
                                    item.children.length > 1 &&
                                    item.children.map((child) => (
                                      <View
                                        key={child.id}
                                        style={[
                                          sy['flex-row'],
                                          sy['items-start'],
                                          sy['p-4'],
                                          sy['pl-12'],
                                        ]}>
                                        <View style={[sy['pr-4']]}>
                                          <RadioButton
                                            checked={
                                              selectedWorkId === child.id
                                            }
                                            onPress={() =>
                                              setSelectedWorkId(child.id)
                                            }
                                          />
                                        </View>
                                        <View style={[sy['flex-1']]}>
                                          <Text>
                                            {child.display_text}
                                            {parts[child.id] && (
                                              <>
                                                &nbsp;(
                                                {parts[child.id].length})
                                              </>
                                            )}
                                          </Text>
                                        </View>
                                        <Text>
                                          {Format.number(child.work_time, 2)}
                                          &nbsp;u
                                        </Text>
                                      </View>
                                    ))}
                                </React.Fragment>
                              );
                            })}
                          </View>
                        </>
                      )}
                      <View
                        style={[sy['flex-row'], sy['items-center'], sy['p-4']]}>
                        <View style={[sy['pr-4']]}>
                          <RadioButton
                            checked={!selectedWorkId}
                            onPress={() => {
                              setSelectedWorkId(null);
                            }}
                          />
                        </View>
                        <View style={[sy['flex-row'], sy['flex-1']]}>
                          <Text style={[sy['flex-1']]}>Invoer</Text>
                          <EditOutlinedIcon />
                        </View>
                      </View>
                    </ScrollView>
                  </View>
                </Content>
                <Divider style={sy['bg-gray88']} />
                <Actions style={[sy['justify-between']]}>
                  <View />
                  <View style={[sy['flex-row'], sy['gap-2']]}>
                    <ActionButton
                      onPress={() => {
                        setSelectedWorkId(selected.reference);
                        setScreen(SCREENS.TASK);
                      }}>
                      Annuleren
                    </ActionButton>
                    <ActionButton
                      onPress={() => {
                        if (selected.reference === selectedWorkId) {
                          // No changes
                          setScreen(SCREENS.TASK);
                          return;
                        }
                        const {work, task_parts, split_parts, rob_code} =
                          buildWorkSelectionUpdate({
                            selected,
                            selectedWorkId,
                            maintenance,
                            parts,
                            is_fleet_car,
                            is_retail_car,
                            rob,
                            codeSuggestions,
                            slaMaintenanceParts,
                          });
                        if (
                          split_parts.length > 0 &&
                          user_settings.never_show_parts_split_dialog !== true
                        ) {
                          setShowPartsSplitDialog(true);
                        }
                        setSelected({
                          ...selected,
                          reference: work?.id,
                          number: work?.number,
                          work_text: work?.text,
                          original_work_time: work ? work.work_time : null,
                          calculated_work_time: null,
                          work_time: work ? work.work_time : null,
                          itemMpId: work?.itemMpId,
                          rob_code,
                          parts: task_parts,
                          split_parts,
                        });
                        setDurations((prevState) => ({
                          ...prevState,
                          [selected.id]: work ? work.work_time : 0,
                        }));
                        setOriginalDurations((prevState) => ({
                          ...prevState,
                          [selected.id]: work ? null : 0,
                        }));
                        setScreen(SCREENS.TASK);
                      }}>
                      OK
                    </ActionButton>
                  </View>
                </Actions>
              </>
            )}
          </>
        )}
      </Actionsheet>
      {showDurationDialog && (
        <DurationDialog
          {...getDurationDialogContext(
            showDurationDialog,
            selected.id,
            priceCheck,
            source,
          )}
          work_time={showDurationDialog.original_work_time}
          calculated_work_time={showDurationDialog.calculated_work_time}
          original_duration={originalDurations[showDurationDialog.task_id]}
          duration={durations[showDurationDialog.task_id]}
          onChange={(duration, type) => {
            let change;
            switch (type) {
              case DURATION_HOUR_TYPES.suggested:
              case DURATION_HOUR_TYPES.work:
              case DURATION_HOUR_TYPES.custom: {
                change = duration;
                break;
              }
              default: {
                change = null;
                break;
              }
            }
            if (change != null) {
              setDurations((prevState) => ({
                ...prevState,
                [showDurationDialog.task_id]: duration,
              }));
              setOriginalDurations((prevState) => ({
                ...prevState,
                [showDurationDialog.task_id]:
                  type === DURATION_HOUR_TYPES.work ? null : change,
              }));
            }
          }}
          onDismiss={() => setShowDurationDialog(null)}
        />
      )}
      {showEditQuantity && (
        <QuantityDialog
          {...showEditQuantity}
          onDismiss={() => setShowEditQuantity(null)}
        />
      )}
      {showWarrantyInfoDialog && (
        <WarrantyInfoDialog
          title={task.description}
          onDismiss={() => setShowWarrantyInfoDialog(false)}
        />
      )}
      <AdjustmentDialogs
        showDiscountDialog={showDiscountDialog}
        setShowDiscountDialog={setShowDiscountDialog}
        showMarkupDialog={showMarkupDialog}
        setShowMarkupDialog={setShowMarkupDialog}
        includeTaskId={false}
      />
      {showDuplicateWarning && (
        <Dialog
          visible={true}
          onDismiss={() => setShowDuplicateWarning(null)}
          buttons={[
            {
              text: 'Annuleren',
              onPress: () => setShowDuplicateWarning(null),
            },
            {
              text: 'Vervangen',
              onPress: () => onDuplicate(),
            },
          ]}>
          <View style={[sy['py-4']]}>
            <Text>Vervangen door {showDuplicateWarning.change}?</Text>
            <Text>({showDuplicateWarning.rob_code} naar vervallen)</Text>
          </View>
        </Dialog>
      )}
      {showPartsSplitDialog && (
        <Dialog
          visible={true}
          onDismiss={() => setShowPartsSplitDialog(false)}
          buttons={[
            {
              text: 'OK',
              onPress: async () => {
                if (user_settings.never_show_parts_split_dialog) {
                  await userApi.update_settings({
                    ...user_settings,
                    never_show_parts_split_dialog: true,
                  });
                }
                setShowPartsSplitDialog(false);
              },
            },
          ]}
          options={{
            noPaddingContent: true,
          }}
          title="Apart toegevoegd"
          titleIcon={<InfoIcon fill="#4a4a49" />}>
          <View style={[sy['py-4'], sy['px-6']]}>
            <Text>
              Onderdelen in gekozen service met eigen ROB activiteit worden
              apart toegevoegd aan werkopdracht.
            </Text>
          </View>
          <Divider style={sy['bg-gray88']} />
          <View style={[sy['py-4'], sy['px-6']]}>
            <View style={[sy['flex-row'], sy['gap-4']]}>
              <Checkbox
                disabledTimeout={0}
                onPress={(value) =>
                  dispatch(
                    setUserSettings({
                      never_show_parts_split_dialog: value,
                    }),
                  )
                }
              />
              <Text>Niet meer tonen</Text>
            </View>
          </View>
        </Dialog>
      )}
      {showDamageNotAllowedDialog && (
        <DamageNotAllowedDialog
          license={license}
          rob={rob}
          request_id={request_id}
          damage_id={damage_id}
          component={component}
          onDismiss={({close_action_sheet}) => {
            setShowDamageNotAllowedDialog(false);
            if (close_action_sheet) {
              onDismiss();
            }
          }}
        />
      )}
    </>
  );
};
Maintenance.propTypes = {
  onDismiss: PropTypes.any,
  dimensions: PropTypes.any,
  license: PropTypes.any,
  is_fleet_car: PropTypes.any,
  is_retail_car: PropTypes.any,
  vin_number: PropTypes.any,
  mileage: PropTypes.any,
  car: PropTypes.any,
  request_id: PropTypes.any,
  damage_id: PropTypes.any,
  dealer_id: PropTypes.any,
  company_id: PropTypes.any,
  lessor: PropTypes.any,
  task: PropTypes.any,
  additional: PropTypes.any,
  rob: PropTypes.any,
  component: PropTypes.any,
  onChange: PropTypes.any,
  onShowDenialReasons: PropTypes.any,
};
export default withDimensions(Maintenance);
